import React from 'react';
import RydRollHeaderGroup from '../widgets/RydRollHeaderGroup';
import NoteDurationSelector from '../pickers/DurationSelector';
import { RydViewport } from '../widgets/RydViewport';
import {
    getRenderedWidthForHNC,
    normalizeClassName
} from '../../render/renderUtils';
import {
    BarSet,
    ChordRollExpandedView,
    ChordRollInputLayerCollpased,
    ToneRollKeybed
} from '../rolls/ChordRoll';
import { ComputedContextFrame } from '../../model/context';
import { RydCollapseControlButton } from '../widgets/RydCollapseControlButton';
import { CaretController } from '../../utils/ScrollController';
import { NoteDuration } from '../../model/core';
import { RydTrackUIState } from '../../model/tracks';
import { useZoomFactor } from '../../stores/ZoomStore';
import { ChordCollapsed } from '../rolls/ChordCollapsed';
import { TonalityHighlightToggle } from '../dialogs/TonalityHighlightToggle';


export function ChordRollStandalone({
    trackId,
    contextFrame,
    newNoteDuration,
    setNewNoteDuration,
    trackUiState,
    setTrackUIState,
    caretController,
    hnc,
    setHnc,
    updateChordRollCB,
    harmonicPredicates
}: {
    trackId: number,
    contextFrame: ComputedContextFrame,
    newNoteDuration: NoteDuration,
    setNewNoteDuration: (duration: NoteDuration) => void,
    trackUiState: RydTrackUIState,
    setTrackUIState?: (state: RydTrackUIState) => void,
    caretController?: CaretController,
    hnc: any,
    setHnc: (hnc: any) => void,
    updateChordRollCB: (start: number, chord: any) => void,
    harmonicPredicates?: any
}) {
    const zoomFactor = useZoomFactor();
    const ppq = hnc.getPPQ();

    const header = <div className="piano-roll-header-info-bar">
        <div className={normalizeClassName(['header-controls', "piano-roll-header-controls"])}>
            <RydRollHeaderGroup>
                {!trackUiState.collapsed &&
                    <>
                        <TonalityHighlightToggle
                            contextFrame={contextFrame}
                            tonalityHighlightVisible={trackUiState.tonalityVisible}
                            setTonalityHighlightVisible={
                                (visible: boolean) => setTrackUIState?.({ ...trackUiState, tonalityVisible: visible })
                            } />
                        <NoteDurationSelector
                            updateSelectedDuratonCB={setNewNoteDuration}
                            duration={newNoteDuration} />
                    </>
                }
                <RydCollapseControlButton
                    collapsed={trackUiState.collapsed}
                    setCollapsed={() => {
                        setTrackUIState?.({ ...trackUiState, collapsed: !trackUiState.collapsed });
                    }} />

            </RydRollHeaderGroup>
        </div>
    </div>;

    const collapsedDisplayHack = trackUiState.collapsed ? {} : { display: 'inline-flex' };

    return (
        <RydViewport
            name="Chord Roll"
            header={header}
            adaptiveHeight={true}
            scrollController={caretController?.scrollController}>
            <div
                className="piano-roll-content-wrapper"
                style={{
                    ...collapsedDisplayHack,
                    width: getRenderedWidthForHNC({notes: hnc, includeKeyboard: true})
                }} >

                {!trackUiState.collapsed &&
                    <ToneRollKeybed
                        tonality={contextFrame.tonality}
                    />}

                {/* Top part, collapsed */}
                <ChordRollInputLayerCollpased
                    hnc={hnc}
                    resolution={newNoteDuration}
                    updateChordRollCB={(start, chord) => updateChordRollCB(start, { chord, duration: hnc.get(start).length > 0 ? hnc.get(start)[0].duration : newNoteDuration })}
                />
                {hnc.getGrid().map((ng, i) => ng.map((n, j) =>
                    <ChordCollapsed
                        key={i}
                        start={i}
                        updateChordCB={(chord) => updateChordRollCB(i, { chord: chord, duration: n.duration })}
                        updateChordDurationCB={(start, duration) => updateChordRollCB(start, { chord: hnc.get(start)[0].payload, duration: duration })}
                        chord={n}
                        ppq={ppq}
                        zoomFactor={zoomFactor}
                    />))}

                {/* Bottom part, expanded */}
                {!trackUiState.collapsed && <ChordRollExpandedView
                    trackId={trackId}
                    hnc={hnc}
                    resolution={newNoteDuration}
                    updateChordRollCB={(start, chord) => updateChordRollCB(start, { chord, duration: hnc.get(start).length > 0 ? hnc.get(start)[0].duration : newNoteDuration })}
                    tonality={contextFrame.tonality}
                    updateHNC={setHnc}
                    harmonicPredicates={harmonicPredicates}
                    timeSignature={contextFrame.timeSignature}
                />}

                <BarSet timeSignature={contextFrame.timeSignature} hnc={hnc} />
            </div>
        </RydViewport>
    );
}